// Wat zweeft: Zweef (trede 1), Info (de i-knop), Paneel (trede 2, niet-modaal,
// native <dialog>) en Dialoog (modaal, voor een vergrote kaart). Esc sluit
// altijd de bovenste laag; de focus gaat terug naar waar hij vandaan kwam.

import { ArrowLeft, Info as InfoIcoon, X } from "lucide-react";
import { type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode, useEffect, useRef, useState } from "react";

import { Icoon, Knop } from "./basis";

/** Zet rekenwaarden als CSS-variabele; de enige toegestane style (DESIGN.md). */
export const vars = (v: Record<string, string | number>): CSSProperties => v as CSSProperties;

// ── Zweef ──────────────────────────────────────────────────────────────────

export function Zweef({ tekst, children, blok = false }: { tekst: ReactNode; children: ReactNode; blok?: boolean }) {
  const doel = useRef<HTMLSpanElement>(null);
  const [plek, setPlek] = useState<{ x: number; y: number; boven: boolean } | null>(null);
  const toon = () => {
    const r = doel.current?.getBoundingClientRect();
    if (!r) return;
    const boven = r.bottom > window.innerHeight - 140;
    setPlek({ x: Math.max(8, Math.min(r.left, window.innerWidth - 308)), y: boven ? r.top - 6 : r.bottom + 6, boven });
  };
  if (!tekst) return <>{children}</>;
  return (
    <span
      ref={doel}
      className="s-zweef-doel"
      data-blok={blok || undefined}
      onMouseEnter={toon}
      onMouseLeave={() => setPlek(null)}
      onFocus={toon}
      onBlur={() => setPlek(null)}
    >
      {children}
      {plek && (
        <span
          role="tooltip"
          data-lat="zweeft"
          className={`s-zweef${plek.boven ? " s-zweef--boven" : ""}`}
          style={vars({ "--zweef-x": `${plek.x}px`, "--zweef-y": `${plek.y}px` })}
        >
          {tekst}
        </span>
      )}
    </span>
  );
}

/** De i-knop: uitleg bij zweven of focus. */
export function Info({ tekst, label = "Uitleg" }: { tekst: ReactNode; label?: string }) {
  return (
    <Zweef tekst={tekst}>
      <button type="button" className="s-knop s-knop--icoon s-knop--klein" aria-label={label} onClick={(e) => e.stopPropagation()}>
        <Icoon als={InfoIcoon} klein />
      </button>
    </Zweef>
  );
}

// ── De stapel lagen voor Esc ───────────────────────────────────────────────

const stapel: { sluit: () => void }[] = [];
function useLaag(open: boolean, onSluit: () => void) {
  const ref = useRef({ sluit: onSluit });
  ref.current.sluit = onSluit;
  useEffect(() => {
    if (!open) return;
    const laag = ref.current;
    stapel.push(laag);
    const vorige = document.activeElement as HTMLElement | null;
    return () => {
      const i = stapel.indexOf(laag);
      if (i >= 0) stapel.splice(i, 1);
      if (vorige && document.contains(vorige)) vorige.focus({ preventScroll: true });
    };
  }, [open]);
}
if (typeof window !== "undefined") {
  window.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || !stapel.length) return;
    // Een open menu gaat voor: dat sluit eerst.
    if (document.querySelector(".s-menu")) return;
    e.preventDefault();
    stapel.at(-1)!.sluit();
  });
}

// ── Paneel ─────────────────────────────────────────────────────────────────

export function Paneel({
  open,
  onSluit,
  niveau = 1,
  kop,
  terug,
  onTerug,
  voet,
  label,
  kopActies,
  greep,
  vol = false,
  children,
}: {
  open: boolean;
  onSluit: () => void;
  /** 2 = een tweede paneel over het eerste (brondetail). */
  niveau?: 1 | 2;
  kop?: ReactNode;
  /** Tekst van de terugknop; zonder = alleen een kruis. */
  terug?: string;
  /** Wat de terugknop (en Esc) doet als dat iets anders is dan sluiten, bv. één
   *  laag terug in de kaart in kaart; het kruis sluit dan alles. */
  onTerug?: () => void;
  voet?: ReactNode;
  label: string;
  /** Extra knoppen vóór het kruis (bv. verbergen). */
  kopActies?: ReactNode;
  /** Met een greep is het paneel aan de linkerrand te slepen. `onSleep` krijgt
   *  de gewenste breedte tijdens het slepen, `onLos` bij het loslaten (dan
   *  beslist de aanroeper: vastklikken, dichtklappen of volle breedte). De
   *  breedte zelf staat als --paneel-b op <html>. */
  greep?: { onSleep: (px: number) => void; onLos: (px: number) => void; onReset: () => void };
  /** Helemaal opengesleept: het paneel ligt over de hele inhoud. */
  vol?: boolean;
  children: ReactNode;
}) {
  useLaag(open, onTerug ?? onSluit);
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (open) ref.current?.querySelector<HTMLElement>(".s-paneel__kop button")?.focus({ preventScroll: true });
  }, [open]);
  if (!open) return null;
  return (
    <dialog ref={ref} open className={`s-paneel${niveau === 2 ? " s-paneel--2" : ""}${vol ? " s-paneel--vol" : ""}`} data-lat="paneel" aria-label={label}>
      {greep && (
        <Sleepgreep
          kant="links"
          label="Breedte van het paneel"
          onSleep={(x) => greep.onSleep(window.innerWidth - x - 8)}
          onLos={(x) => greep.onLos(window.innerWidth - x - 8)}
          onReset={greep.onReset}
        />
      )}
      <div className="s-paneel__kop">
        {terug && (
          <Knop icoon={ArrowLeft} onClick={onTerug ?? onSluit}>
            {terug}
          </Knop>
        )}
        <div className="s-paneel__kop-tekst">{kop}</div>
        {kopActies}
        <Knop variant="icoon" aria-label="Sluiten" onClick={onSluit} icoon={X} />
      </div>
      <div className="s-paneel__inhoud">{children}</div>
      {voet && <div className="s-paneel__voet">{voet}</div>}
    </dialog>
  );
}

// ── Sleepgreep en magneet ──────────────────────────────────────────────────
// Zoals de zijbalken van ChatGPT en Codex: slepen volgt de muis, bij het
// loslaten klikt de rand vast op de dichtstbijzijnde stand (magnetisch), en
// wie tot tegen de zijkant sleept, klapt het vlak helemaal in (of, bij het
// paneel, helemaal open tot de zijkant).

/** Klikt vast op de dichtstbijzijnde stand binnen `bereik` px. */
export function magneet(px: number, standen: readonly number[], bereik = 28): number {
  let beste = px;
  let afstand = bereik;
  for (const st of standen) {
    const a = Math.abs(px - st);
    if (a < afstand) {
      afstand = a;
      beste = st;
    }
  }
  return Math.round(beste);
}

/** Laat een vlak na het loslaten 180 ms soepel naar zijn stand schuiven. */
export function snapAnimatie() {
  const html = document.documentElement;
  html.dataset.snapt = "ja";
  window.setTimeout(() => delete html.dataset.snapt, 200);
}

/** Paneelbreedte bij het loslaten: dicht, vol, of vastgeklikt op 30/40/50/60%. */
export function paneelLos(px: number, beschikbaar: number): "dicht" | "vol" | number {
  const w = window.innerWidth;
  if (px < 260) return "dicht";
  if (px > beschikbaar * 0.85) return "vol";
  return Math.max(320, magneet(px, [0.3, 0.4, 0.5, 0.6].map((p) => w * p)));
}

/** Een smalle greep op de rand van een vlak. Slepen meldt de x van de muis,
 *  loslaten ook; dubbelklik zet terug naar de standaard; pijltjes werken ook. */
export function Sleepgreep({
  kant,
  label,
  onSleep,
  onLos,
  onReset,
}: {
  kant: "links" | "rechts";
  label: string;
  onSleep: (clientX: number) => void;
  onLos?: (clientX: number) => void;
  onReset?: () => void;
}) {
  const start = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture?.(e.pointerId);
    document.documentElement.dataset.sleept = "ja";
    const beweeg = (ev: PointerEvent) => onSleep(ev.clientX);
    const los = (ev: PointerEvent) => {
      el.removeEventListener("pointermove", beweeg);
      el.removeEventListener("pointerup", los);
      delete document.documentElement.dataset.sleept;
      snapAnimatie();
      onLos?.(ev.clientX);
    };
    el.addEventListener("pointermove", beweeg);
    el.addEventListener("pointerup", los);
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      tabIndex={0}
      className={`s-greep s-greep--${kant}`}
      onPointerDown={start}
      onDoubleClick={onReset}
      onKeyDown={(e) => {
        const r = e.currentTarget.getBoundingClientRect().left + 5;
        const x = e.key === "ArrowLeft" ? r - 24 : e.key === "ArrowRight" ? r + 24 : null;
        if (x === null) return;
        e.preventDefault();
        onSleep(x);
        onLos?.(x);
      }}
    />
  );
}

// ── Dialoog ────────────────────────────────────────────────────────────────

export function Dialoog({
  open,
  onSluit,
  titel,
  sub,
  children,
}: {
  open: boolean;
  onSluit: () => void;
  titel: string;
  sub?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useLaag(open, onSluit);
  useEffect(() => {
    const el = ref.current;
    if (!el || !open) return;
    if (typeof el.showModal === "function" && !el.open) {
      try {
        el.showModal();
      } catch {
        el.setAttribute("open", "");
      }
    } else el.setAttribute("open", "");
    const annuleer = (e: Event) => {
      e.preventDefault();
      onSluit();
    };
    el.addEventListener("cancel", annuleer);
    return () => el.removeEventListener("cancel", annuleer);
  }, [open, onSluit]);
  if (!open) return null;
  return (
    <dialog
      ref={ref}
      className="s-dialoog"
      aria-label={titel}
      data-lat="zweeft"
      onClick={(e) => e.target === e.currentTarget && onSluit()}
    >
      <div className="s-dialoog__kop">
        <div className="s-strook__inhoud">
          <h2 className="s-dialoog__titel">{titel}</h2>
          {sub && <p className="s-dialoog__sub">{sub}</p>}
        </div>
        <Knop variant="icoon" aria-label="Sluiten" onClick={onSluit} icoon={X} />
      </div>
      <div className="s-dialoog__inhoud">{children}</div>
    </dialog>
  );
}
